import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MapPin, Loader2, Navigation, Clock, Star, Crosshair } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/lib/apiConfig";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

type CityItem = {
  id: number | string;
  title: string;
  stateId?: number | string;
  stateName?: string;
};

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (city: CityItem) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
  /** Override the input's own size classes — default suits a large hero search bar; a
   * compact filter bar (e.g. the listings page toolbar) should pass a shorter height. */
  inputClassName?: string;
}

// No hardcoded cities - all cities come from database

const CityAutocomplete = ({
  value,
  onChange,
  onSelect,
  onKeyPress,
  placeholder = "City",
  className = "",
  inputClassName = "h-14 text-base",
}: CityAutocompleteProps) => {
  const [cities, setCities] = useState<CityItem[]>([]);
  const [filteredCities, setFilteredCities] = useState<CityItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentCities, setRecentCities] = useState<string[]>([]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateDropdownPosition = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    const width = Math.max(280, rect.width);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const top = rect.bottom + 12;

    setDropdownPos({ top, left, width });
  };

  // Detect current location using Geolocation API
  const detectCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location detection");
      return;
    }

    setIsDetectingLocation(true);
    toast.info("Detecting your location...", { id: "location-detect" });
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Use Nominatim (OpenStreetMap) for reverse geocoding - free, no API key needed
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en',
              }
            }
          );
          
          if (!response.ok) throw new Error("Location fetch failed");
          
          const data = await response.json();
          const address = data.address;
          
          // Try to get city name from different fields
          const cityName = address.city || address.town || address.village || address.county || address.state_district;
          
          if (cityName) {
            onChange(cityName);
            setShowSuggestions(false);
            saveRecentCity(cityName);
            
            // Find matching city from database to trigger onSelect with full city data
            const matchedCity = cities.find(c => 
              c.title.toLowerCase() === cityName.toLowerCase()
            );
            
            if (matchedCity && onSelect) {
              onSelect(matchedCity);
            }
            
            toast.success(`Location detected: ${cityName}`, { id: "location-detect" });
          } else {
            toast.error("Could not detect city name from your location", { id: "location-detect" });
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          toast.error("Failed to detect city from your location", { id: "location-detect" });
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        setIsDetectingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location permission denied. Please allow location access in your browser settings.", { id: "location-detect" });
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable", { id: "location-detect" });
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out", { id: "location-detect" });
            break;
          default:
            toast.error("Unable to detect your location", { id: "location-detect" });
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  };

  // Load cities from PHP/MySQL API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        let raw: any[] = [];
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}?action=cities`);
        const data = await response.json();
        raw = Array.isArray(data?.data) ? data.data : [];
        const cityList: CityItem[] = raw
          .map((city: any) => {
            if (!city || typeof city !== 'object') return null;
            const title = String(city.title || city.name || city.city_name || '').trim();
            const id = city.id; // uuid or number — kept as-is
            const stateId = city.state_id ?? city.stateId;
            const stateName = String(city.state_name || city.stateName || city.state || '').trim() || undefined;
            if (!title || id == null) return null;
            return { id: id as any, title, stateId: stateId as any, stateName };
          })
          .filter(Boolean) as CityItem[];
        setCities(cityList);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      }
    };

    fetchCities();
    
    // Load recent cities from localStorage
    const saved = localStorage.getItem('patakare_recent_cities');
    if (saved) {
      try {
        setRecentCities(JSON.parse(saved).slice(0, 3));
      } catch {
        setRecentCities([]);
      }
    }
  }, []);

  // Filter cities based on input
  useEffect(() => {
    if (value.length === 0) {
      // Show recent cities + first few database cities when empty
      const recent = recentCities
        .map((title) => cities.find((c) => c.title.toLowerCase() === title.toLowerCase()))
        .filter(Boolean) as CityItem[];
      
      // Get top cities from database that are not in recent
      const topDbCities = cities
        .filter(c => !recentCities.some(r => r.toLowerCase() === c.title.toLowerCase()))
        .slice(0, 8 - recent.length);
      
      setFilteredCities([...recent, ...topDbCities].slice(0, 8));
    } else {
      setIsLoading(true);
      const query = value.toLowerCase();
      const filtered = cities
        .filter(city => {
          const cityName = city.title;
          const stateName = city.stateName || '';
          return cityName.toLowerCase().includes(query) || stateName.toLowerCase().includes(query);
        })
        .slice(0, 8);
      
      setFilteredCities(filtered);
      setIsLoading(false);
    }
  }, [value, cities, recentCities]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInInput = !!wrapperRef.current && wrapperRef.current.contains(target);
      const clickedInDropdown = !!dropdownRef.current && dropdownRef.current.contains(target);
      if (!clickedInInput && !clickedInDropdown) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep dropdown above hero/stats (avoid stacking-context/z-index issues)
  useEffect(() => {
    if (!showSuggestions) {
      setDropdownPos(null);
      return;
    }

    updateDropdownPosition();
    const handler = () => updateDropdownPosition();

    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [showSuggestions, filteredCities.length]);

  const saveRecentCity = (cityName: string) => {
    const updated = [cityName, ...recentCities.filter(c => c !== cityName)].slice(0, 3);
    setRecentCities(updated);
    localStorage.setItem('patakare_recent_cities', JSON.stringify(updated));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleCityClick = (city: CityItem) => {
    onChange(city.title);
    onSelect?.(city);
    setShowSuggestions(false);
    saveRecentCity(city.title);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filteredCities.length === 0) {
      onKeyPress?.(e);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => (prev < filteredCities.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        if (selectedIndex >= 0 && filteredCities[selectedIndex]) {
          e.preventDefault();
          handleCityClick(filteredCities[selectedIndex]);
        } else {
          onKeyPress?.(e);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        onKeyPress?.(e);
    }
  };

  const isRecent = (cityTitle: string) => recentCities.includes(cityTitle);
  const isFromDatabase = (cityId: number | string) => cities.some(c => c.id === cityId);

  return (
    <div ref={wrapperRef} className={`relative flex-1 group ${className}`}>
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors z-10" />
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowSuggestions(true)}
        data-testid="city-location-input"
        className={`pl-12 border-2 border-border focus:border-primary rounded-xl bg-white transition-all ${inputClassName}`}
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
        </div>
      )}

      {/* Suggestions dropdown (portaled so it never goes behind hero/stats) */}
      {typeof document !== "undefined" && dropdownPos &&
        createPortal(
          <AnimatePresence>
            {showSuggestions && filteredCities.length > 0 && (
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="fixed rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-gray-100 overflow-hidden z-[9999]"
                style={{
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  backgroundColor: "#ffffff",
                }}
              >
            {/* Header with Detect Location */}
            <div className="px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#ffffff' }}>
              <button
                onClick={detectCurrentLocation}
                disabled={isDetectingLocation}
                className="w-full flex items-center gap-3 text-left hover:bg-primary/5 rounded-lg p-2 -m-1 transition-colors disabled:opacity-50"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                  {isDetectingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Crosshair className="w-4 h-4" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {isDetectingLocation ? "Detecting..." : "Use Current Location"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Detect your city via GPS
                  </div>
                </div>
              </button>
            </div>

            {/* Section Header */}
            <div className="px-4 py-2 border-b border-gray-50" style={{ backgroundColor: '#ffffff' }}>
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Navigation className="w-3.5 h-3.5 text-primary" />
                <span>{value.length === 0 ? "Select a City" : "Matching Cities"}</span>
              </div>
            </div>

            {/* Cities list */}
            <div className="py-2 max-h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent" style={{ backgroundColor: '#ffffff' }}>
              {filteredCities.map((city, index) => (
                <motion.button
                  key={city.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => handleCityClick(city)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 ${
                    selectedIndex === index 
                      ? "bg-primary/5 border-l-2 border-l-primary" 
                      : "hover:bg-gray-50 border-l-2 border-l-transparent"
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isRecent(city.title) 
                      ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white" 
                      : isFromDatabase(city.id)
                        ? "bg-gradient-to-br from-primary to-cyan-500 text-white"
                        : "bg-gray-100 text-gray-500"
                  }`}>
                    {isRecent(city.title) ? (
                      <Clock className="w-4 h-4" />
                    ) : isFromDatabase(city.id) ? (
                      <Star className="w-4 h-4" />
                    ) : (
                      <MapPin className="w-4 h-4" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm">
                      {city.title}
                    </div>
                    {city.stateName && (
                      <div className="text-[11px] text-muted-foreground">
                        {city.stateName}
                      </div>
                    )}
                  </div>

                  {/* Badge */}
                  {isRecent(city.title) && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      Recent
                    </span>
                  )}
                </motion.button>
              ))}
            </div>
            
            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <Navigation className="w-3 h-3 text-primary" />
                </div>
                <span>Type to search from 1000+ cities</span>
              </div>
            </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

    </div>
  );
};

export default CityAutocomplete;
